import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { _isEqual, _sortBy } from '@shared/libs/lodash'
import {
  createPreset,
  deleteFavoritePreset as deleteFavoritePresetRequest,
  deletePreset,
  getFavoritePresets as getFavoritePresetsRequest,
  getPresets,
  setFavoritePreset as setFavoritePresetRequest,
  updatePreset,
} from '@gu-datagrid-new/api/datagrid.api'
import { useToast } from '@shared/gu-components/toast'
import { useGridState } from '@gu-datagrid-new/composables/GridState'
import { error } from '@shared/global/console-messages'

export function useFilterPresets(props, emits) {
  const { getCurrentGridState, saveCurrentGridState } = useGridState()
  const route = useRoute()
  const { toast } = useToast()

  const PRESET_STATE = Object.freeze({
    NONE: 0,
    SELECTED: 1,
    CREATE: 2,
    EDIT: 3,
    COPY: 4,
  })

  const PRESET_SHARE_LEVEL = {
    PRIVATE: 'PRIVATE',
    PUBLIC: 'PUBLIC',
    ROLE: 'ROLE',
  }

  const state = ref(PRESET_STATE.NONE)
  const selectedPresetId = ref(null)
  const selectedPreset = ref(null)
  const newPresetName = ref(null)
  const newShareLevel = ref(null)
  const newPresetSiteScopes = ref([])
  const newPresetRoles = ref([])
  const presets = ref([])
  const loading = ref(false)
  const sideDrawerHeaderText = ref('')
  const isSideDrawerOpen = ref(false)
  const isDeleteDialogOpen = ref(false)
  const isPresetsDialogOpen = ref(false)
  const favoritePresetId = ref(null)

  const allowedCreateEditPresetShareLevels = computed(() => {
    if (props.admin) {
      return [
        PRESET_SHARE_LEVEL.PRIVATE,
        PRESET_SHARE_LEVEL.PUBLIC,
        PRESET_SHARE_LEVEL.ROLE,
      ]
    }
    return [PRESET_SHARE_LEVEL.PRIVATE]
  })

  const userCanEditPreset = computed(() => {
    if (selectedPreset.value && selectedPreset.value.shareLevel === PRESET_SHARE_LEVEL.PRIVATE) return true
    return props.admin
  })

  const saveAllowed = computed(() => {
    if (loading.value) return false

    if (inEditMode.value) {
      if (selectedPreset.value?.name !== newPresetName.value) return true
      if (selectedPreset.value?.shareLevel !== newShareLevel.value) return true
      if (selectedPreset.value && selectedPreset.value.scopes) {
        if (!_isEqual(_sortBy([...selectedPreset.value.scopes]), _sortBy([...newPresetSiteScopes.value]))) return true
      }
      if (selectedPreset.value && selectedPreset.value.roles) {
        if (!_isEqual(_sortBy([...selectedPreset.value.roles]), _sortBy([...newPresetRoles.value]))) return true
      }

      // Enable Save when grid state differs from preset
      if (selectedPreset.value && props.gridApi) {
        const current = getCurrentGridState(props.gridApi)
        const preset = selectedPreset.value
        const filterEqual = JSON.stringify(current?.agGrid?.filterModel) === JSON.stringify(preset?.agGrid?.filterModel)
        const columnEqual = JSON.stringify(current?.agGrid?.columnState) === JSON.stringify(preset?.agGrid?.columnState)
        const quickFilterEqual = (current?.quickFilterValue ?? '') === (preset?.quickFilterValue ?? '')
        if (!filterEqual || !columnEqual || !quickFilterEqual) return true
      }
    }

    return props.gridChangedFromPreset
  })

  const inCopyMode = computed(() => state.value === PRESET_STATE.COPY)
  const inCreateMode = computed(() => state.value === PRESET_STATE.CREATE)
  const inEditMode = computed(() => state.value === PRESET_STATE.EDIT)
  const inNoneMode = computed(() => state.value === PRESET_STATE.NONE)
  const inSelectedMode = computed(() => state.value === PRESET_STATE.SELECTED)

  const showToast = (config) => {
    toast({
      variant: 'info',
      dismissable: true,
      ...config,
    })
  }

  const presetsByType = (type) => {
    return !presets.value || !type
      ? []
      : presets.value.filter(({ shareLevel }) => shareLevel.toUpperCase() === type.toUpperCase())
  }

  const clear = () => {
    selectedPresetId.value = null
    newPresetName.value = null
    newPresetRoles.value = []
    newPresetSiteScopes.value = []
    newShareLevel.value = null
    state.value = PRESET_STATE.NONE
    isSideDrawerOpen.value = false

    emits('clearPreset')
  }

  const initializeNewPreset = () => {
    sideDrawerHeaderText.value = 'Create Preset'
    isPresetsDialogOpen.value = false
    isSideDrawerOpen.value = true
    newPresetName.value = null
    newShareLevel.value = PRESET_SHARE_LEVEL.PRIVATE
    const firstScope = props.siteScopes.length ? props.siteScopes[0] : null
    newPresetSiteScopes.value = firstScope ? [typeof firstScope === 'object' ? firstScope.value : firstScope] : []
    newPresetRoles.value = []
    state.value = PRESET_STATE.CREATE
  }

  const copy = () => {
    sideDrawerHeaderText.value = 'Create Preset'
    isPresetsDialogOpen.value = false
    isSideDrawerOpen.value = true
    newPresetName.value = `Copy of ${selectedPreset.value.name}`
    newShareLevel.value = PRESET_SHARE_LEVEL.PRIVATE
    const firstScope = props.siteScopes.length ? props.siteScopes[0] : null
    newPresetSiteScopes.value = firstScope ? [typeof firstScope === 'object' ? firstScope.value : firstScope] : []
    newPresetRoles.value = selectedPreset.value.roles || []
    state.value = PRESET_STATE.COPY
  }

  const create = async () => {
    if (!newPresetName.value) {
      showToast({
        variant: 'danger',
        description: 'Error creating preset: No preset name defined. Please set a name for the preset',
      })
      return
    }

    if (!newPresetSiteScopes.value.length) {
      showToast({
        variant: 'danger',
        description: 'Error creating preset: No siteScopes set. At least one scope must be selected',
      })
      return
    }

    if (newShareLevel.value === PRESET_SHARE_LEVEL.ROLE && newPresetRoles.value.length < 1) {
      showToast({
        variant: 'danger',
        description: 'Error creating preset: At least one role must be selected',
      })
      return
    }

    const newPreset = {
      ...getCurrentGridState(props.gridApi),
      name: newPresetName.value,
      roles: newPresetRoles.value,
      shareLevel: newShareLevel.value,
      scopes: newPresetSiteScopes.value,
    }

    if (newPreset.id) {
      delete newPreset.id
    }

    loading.value = true
    try {
      const data = await createPreset(newPreset)
      showToast({
        variant: 'success',
        duration: 5000,
        description: 'Successfully created new preset',
      })

      presets.value.push(data)
      selectedPresetId.value = data.id
      selectedPreset.value = data
      state.value = PRESET_STATE.SELECTED

      saveCurrentGridState(null, { presetId: data.id })
      emits('presetCreated', selectedPreset.value)
    } catch (error) {
      error({
        context: 'Create Preset',
        message: `Error creating preset: ${error.message ?? error}`,
      })
      showToast({
        variant: 'danger',
        description: `Error creating preset: ${error.message ?? error}`,
      })
    } finally {
      loading.value = false
      isSideDrawerOpen.value = false
    }
  }

  const handleDeletePreset = async () => {
    loading.value = true
    try {
      await deletePreset(selectedPresetId.value)
      showToast({
        variant: 'success',
        duration: 5000,
        description: 'Successfully deleted preset',
      })

      const index = presets.value.findIndex(({ id }) => id === selectedPresetId.value)
      if (index > -1) presets.value.splice(index, 1)

      selectedPresetId.value = null
      selectedPreset.value = null
      state.value = PRESET_STATE.NONE
      isDeleteDialogOpen.value = false

      emits('presetDeleted')
    } catch (error) {
      console.error('deletePreset', error)
      showToast({
        variant: 'danger',
        description: `Error deleting preset: ${error.message}`,
      })
    } finally {
      loading.value = false
      isSideDrawerOpen.value = false
    }
  }

  const startEditing = () => {
    sideDrawerHeaderText.value = 'Edit Preset'
    isPresetsDialogOpen.value = false
    isSideDrawerOpen.value = true
    newPresetName.value = selectedPreset.value.name
    newShareLevel.value = selectedPreset.value.shareLevel
    newPresetSiteScopes.value = selectedPreset.value.scopes || []
    newPresetRoles.value = selectedPreset.value.roles || []
    state.value = PRESET_STATE.EDIT
  }

  const update = async () => {
    if (!newPresetName.value) {
      showToast({
        variant: 'danger',
        description: 'Error updating preset: No preset name defined. Please set a name for the preset',
      })
      return
    }

    if (!newPresetSiteScopes.value.length) {
      showToast({
        variant: 'danger',
        description: 'Error updating preset: No siteScopes set. At least one scope must be selected',
      })
      return
    }

    if (newShareLevel.value === PRESET_SHARE_LEVEL.ROLE && newPresetRoles.value.length < 1) {
      showToast({
        variant: 'danger',
        description: 'Error updating preset: No roles set. At least one role must be selected',
      })
      return
    }

    const updatedPreset = {
      ...selectedPreset.value,
      ...getCurrentGridState(props.gridApi),
      id: selectedPresetId.value,
      name: newPresetName.value,
      shareLevel: newShareLevel.value,
      scopes: newPresetSiteScopes.value,
      roles: newPresetRoles.value?.length ? newPresetRoles.value : null,
    }

    loading.value = true
    try {
      await updatePreset(updatedPreset)
      presets.value = presets.value.map(preset => (preset.id === selectedPresetId.value ? updatedPreset : preset))
      selectedPreset.value = updatedPreset

      showToast({
        variant: 'success',
        duration: 5000,
        description: 'Successfully updated preset',
      })

      saveCurrentGridState(null, { presetId: selectedPresetId.value })
      setPreset(selectedPresetId.value)
    } catch (error) {
      console.error('updatePreset', error)
      showToast({
        variant: 'danger',
        description: `Error updating preset: ${error.message}`,
      })
    } finally {
      loading.value = false
      isSideDrawerOpen.value = false
    }
  }

  const closeSideDrawer = () => {
    if (inCreateMode.value) state.value = PRESET_STATE.NONE
    if (inEditMode.value || inCopyMode.value) state.value = PRESET_STATE.SELECTED
    isSideDrawerOpen.value = false
  }

  const setPreset = (presetId) => {
    selectedPresetId.value = presetId
    selectedPreset.value = presets.value.find(p => p.id === presetId) || {}
    state.value = PRESET_STATE.SELECTED

    const presetState = {
      agGrid: {
        filterModel: selectedPreset.value?.agGrid?.filterModel,
        columnState: selectedPreset.value?.agGrid?.columnState,
      },
      quickFilterValue: '',
      id: selectedPreset.value?.id,
      page: 0,
    }

    saveCurrentGridState(null, { presetId: presetState.id })
    emits('setPreset', presetState)
  }

  const loadPresets = async (scope) => {
    if (!scope) {
      throw new Error('Scope is required for the preset manager to function. Please define a primary "scope" in presetsSettings.')
    }

    if (!props.siteScopes.length) {
      console.warn('No site scopes were returned. Please define a list of "siteScopes" in the content override for the preset manager to function appropriately')
    }

    loading.value = true
    try {
      const data = await getPresets(scope)
      presets.value = data

      if (!data?.length) {
        console.debug(`No presets filters returned. Current preset site scope: ${scope}`)
      }

      if (props.pageLoadPresetId) {
        selectedPreset.value = presets.value.find(p => p.id === props.pageLoadPresetId)
        selectedPresetId.value = selectedPreset.value?.id || null
        state.value = selectedPreset.value ? PRESET_STATE.SELECTED : PRESET_STATE.NONE
        if (!selectedPreset.value) {
          console.warn(`No preset found for viewId: ${props.pageLoadPresetId}`)
        }
      }

      emits('presetsLoaded', selectedPreset.value)
    } catch (error) {
      showToast({
        variant: 'danger',
        title: 'Error',
        description: `Error getting presets: ${error.message}`,
      })
    } finally {
      loading.value = false
    }
  }

  const fetchFavoritePreset = async () => {
    try {
      const response = await getFavoritePresetsRequest()
      const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []

      if (!data.length) return null

      const favoritePreset = data.find(pref => pref.preferenceKey === 'favoritePreset')
      if (!favoritePreset) return null

      const value = Number(favoritePreset.preferenceValue)

      return {
        ...favoritePreset,
        preferenceValue: value,
      }
    } catch (err) {
      showToast({
        variant: 'danger',
        description: 'Error getting favorite preset',
      })

      error({
        context: 'Favorite Preset - Get',
        message: err.message || 'Unknown error',
      })

      return null
    }
  }

  const deleteFavoritePreset = async (presetId) => {
    try {
      await deleteFavoritePresetRequest()
      if (presetId) await setFavoritePreset(presetId)
      else favoritePresetId.value = null
    } catch (err) {
      showToast({
        variant: 'danger',
        description: 'Error deleting favorite preset',
      })

      error({
        context: 'Favorite Preset - Delete',
        message: err.message ?? err,
      })
    }
  }

  const setFavoritePreset = async (presetId) => {
    try {
      const hasFavoritePreset = await fetchFavoritePreset()

      if (hasFavoritePreset) await deleteFavoritePreset()

      await setFavoritePresetRequest(presetId)
      favoritePresetId.value = presetId
    } catch (err) {
      showToast({
        variant: 'danger',
        description: 'Error setting favorite preset',
      })

      error({
        context: 'Favorite Preset - Set',
        message: err.message ?? err,
      })
    }
  }

  const fetchAndSaveFavoritePreset = async () => {
    const favoritePreset = await fetchFavoritePreset()
    if (favoritePreset) {
      favoritePresetId.value = favoritePreset.preferenceValue
      return favoritePreset.preferenceValue
    }
    return null
  }

  const initializePresets = async () => {
    if (!props.scope) return

    await loadPresets(props.scope)

    const hasRoutePreset = !!route.query?.presetId
    const hasRouteFilter = !!route.query?.filter

    const favoritePresetIdValue = await fetchAndSaveFavoritePreset()

    if (hasRoutePreset) {
      setPreset(Number(route.query.presetId))
      return
    }

    if (props.pageLoadPresetId) {
      setPreset(Number(props.pageLoadPresetId))
      return
    }

    if (!hasRouteFilter && favoritePresetIdValue) setPreset(favoritePresetIdValue)
  }

  onBeforeMount(initializePresets)

  watch(() => props.scope, (newScope) => {
    if (newScope) {
      loadPresets(newScope)
    }
  })

  return {
    PRESET_SHARE_LEVEL,
    selectedPresetId,
    selectedPreset,
    newPresetName,
    newShareLevel,
    newPresetSiteScopes,
    newPresetRoles,
    presets,
    loading,
    sideDrawerHeaderText,
    isSideDrawerOpen,
    isDeleteDialogOpen,
    isPresetsDialogOpen,
    allowedCreateEditPresetShareLevels,
    userCanEditPreset,
    saveAllowed,
    inCopyMode,
    inCreateMode,
    inEditMode,
    inNoneMode,
    inSelectedMode,
    favoritePresetId,
    clear,
    closeSideDrawer,
    copy,
    create,
    deleteFavoritePreset,
    handleDeletePreset,
    initializeNewPreset,
    presetsByType,
    setFavoritePreset,
    setPreset,
    startEditing,
    update,
  }
}
